import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { normalizeFeatureForUi } from '../utils/featureNormalization';
import { compareAlphabetical, sortByName } from '../utils/sortingUtils';

export default function FeaturePalette({ 
    familyId, 
    familyName,
    onFeatureSelected, 
    placedFeatureNames = [],
    projectId,
    linkedFamilies = []
}) {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all'); // all, shell, complete
    const [expandedCategory, setExpandedCategory] = useState(null);

    // Fetch features from RuleEngine.Api
    const loadFeatures = async () => {
        let targetFamilyId = familyId;
        
        // Check if familyId is valid (not null, undefined, empty, or empty guid)
        const isEmptyGuid = familyId && familyId.toString().toLowerCase() === '00000000-0000-0000-0000-000000000000';
        
        // If familyId is invalid but we have a familyName, try to find the family by name
        if ((!familyId || isEmptyGuid) && familyName) {
            console.log('FeaturePalette: familyId invalid, trying to find by name:', familyName);
            try {
                // Try to find family by name
                const url = projectId ? `/api/products?projectId=${projectId}` : '/api/products';
                const familiesResponse = await apiFetch(url);
                if (familiesResponse.ok) {
                    const allFamilies = await familiesResponse.json();
                    const matchingFamily = allFamilies.find(f => f.name === familyName);
                    if (matchingFamily) {
                        targetFamilyId = matchingFamily.id;
                        console.log('FeaturePalette: Found family by name, ID:', targetFamilyId);
                    } else {
                        console.log('FeaturePalette: No family found with name:', familyName);
                    }
                }
            } catch (err) {
                console.error('FeaturePalette: Failed to find family by name:', err);
            }
        }
        
        if (!targetFamilyId || targetFamilyId.toString().toLowerCase() === '00000000-0000-0000-0000-000000000000') {
            console.log('FeaturePalette: No valid familyId after all attempts. familyId:', familyId, 'familyName:', familyName);
            setFeatures([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Fetch features for the selected family using family-scoped endpoint
            console.log('FeaturePalette: Loading features for familyId:', targetFamilyId);
            const response = await apiFetch(`/api/products/${targetFamilyId}/features`);
            console.log('FeaturePalette: API response status:', response.status);
            if (response.ok) {
                const data = await response.json();
                console.log('FeaturePalette: Received features:', data.length, 'items');
                
                // Classify features as shell or complete
            const classifiedFeatures = data.map(f => {
                const normalizedFeature = normalizeFeatureForUi(f);
                const isShell = f.purpose === 'gui' && 
                               (!normalizedFeature.values || normalizedFeature.values.length === 0) &&
                               !f.rules;
                
                return {
                    ...normalizedFeature,
                    isShell,
                    status: isShell ? 'shell' : 'complete'
                };
            });
                
                setFeatures(classifiedFeatures);
            } else {
                const errorText = await response.text();
                console.error('FeaturePalette: API returned error:', response.status, errorText);
                throw new Error(`Failed to load features: ${response.status} - ${errorText}`);
            }
        } catch (err) {
            setError(err.message);
            console.error('FeaturePalette: Error loading features:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('FeaturePalette useEffect: familyId =', familyId, 'linkedFamilies =', linkedFamilies);
        loadFeatures();
    }, [familyId, linkedFamilies]);

    // Group features by category (InputType)
    const groupedFeatures = features.reduce((groups, feature) => {
        const category = feature.inputType || 'Unknown';
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(feature);
        return groups;
    }, {});

    // Filter features based on search and type, then sort alphabetically
    const filteredFeatures = Object.entries(groupedFeatures).map(([category, featuresInCategory]) => {
        const filtered = sortByName(
            featuresInCategory.filter(f => {
                const matchesSearch = searchQuery 
                    ? f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      (f.displayText || '').toLowerCase().includes(searchQuery.toLowerCase())
                    : true;
                
                const matchesFilter = filterType === 'all' 
                    ? true 
                    : f.status === filterType;
                
                const notPlaced = !placedFeatureNames.includes(f.name);
                
                return matchesSearch && matchesFilter && notPlaced;
            }),
            'name'
        );
        
        return { category, features: filtered };
    })
        .filter(group => group.features.length > 0)
        .sort((a, b) => compareAlphabetical(a.category, b.category));

    const handleDragStart = (e, feature) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({
            type: 'feature',
            featureName: feature.name,
            featureId: feature.id,
            displayText: feature.displayText || feature.name,
            inputType: feature.inputType
        }));
    };

    const handleFeatureClick = (feature) => {
        if (onFeatureSelected) {
            onFeatureSelected(feature);
        }
    };

    // Input type metadata for display
    const inputTypeMetadata = {
        Dropdown: { label: 'Dropdown', icon: 'fa-solid fa-chevron-down', color: '#2563eb' },
        Radio: { label: 'Radio', icon: 'fa-solid fa-circle-dot', color: '#16a34a' },
        Checkbox: { label: 'Checkbox', icon: 'fa-solid fa-square-check', color: '#7c3aed' },
        MultiCheckbox: { label: 'Multi Checkbox', icon: 'fa-solid fa-check-square', color: '#db2777' },
        Slider: { label: 'Slider', icon: 'fa-solid fa-sliders', color: '#ea580c' },
        TextInput: { label: 'Text Input', icon: 'fa-solid fa-font', color: '#059669' },
        NumberInput: { label: 'Number', icon: 'fa-solid fa-hashtag', color: '#0891b2' },
        TextArea: { label: 'Text Area', icon: 'fa-solid fa-paragraph', color: '#64748b' },
        DatePicker: { label: 'Date', icon: 'fa-solid fa-calendar', color: '#84cc16' },
        ColorPicker: { label: 'Color', icon: 'fa-solid fa-palette', color: '#ec4899' },
        ImageDisplay: { label: 'Image', icon: 'fa-solid fa-image', color: '#f59e0b' }
    };

    // Toggle category expansion
    const toggleCategory = (category) => {
        setExpandedCategory(expandedCategory === category ? null : category);
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            height: '100%'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '1rem',
                borderBottom: '1px solid var(--border)'
            }}>
                <h3 style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <i className="fa-solid fa-list" style={{ color: 'var(--text-secondary)' }}></i>
                    Feature Palette
                </h3>
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    background: 'var(--bg-secondary)',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px'
                }}>
                    {features.length} features
                </span>
            </div>

            {/* Search and Filter */}
            <div style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center'
            }}>
                <div style={{ flex: 1 }}>
                    <input
                        type="text"
                        placeholder="Search features..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: '1px solid var(--border)',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            background: 'var(--bg-primary)',
                            color: 'var(--text-primary)'
                        }}
                    />
                </div>
                <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    style={{
                        padding: '0.5rem 0.75rem',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer'
                    }}
                >
                    <option value="all">All Features</option>
                    <option value="complete">Complete Only</option>
                    <option value="shell">Shell Only</option>
                </select>
            </div>

            {/* Content */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
            }}>
                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)'
                    }}>
                        <div className="loader"></div>
                    </div>
                ) : error ? (
                    <div style={{
                        padding: '1rem',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        color: 'var(--text-error, #ef4444)',
                        textAlign: 'center',
                        fontSize: '0.85rem'
                    }}>
                        <i className="fa-solid fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
                        Failed to load features: {error}
                        <button 
                            onClick={loadFeatures}
                            style={{
                                marginLeft: '1rem',
                                padding: '0.25rem 0.5rem',
                                background: 'var(--text-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.75rem'
                            }}
                        >
                            <i className="fa-solid fa-refresh" style={{ marginRight: '0.25rem' }}></i> Retry
                        </button>
                    </div>
                ) : filteredFeatures.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic'
                    }}>
                        <i className="fa-solid fa-search" style={{ 
                            fontSize: '2rem', 
                            marginBottom: '1rem',
                            opacity: 0.5
                        }}></i>
                        <p style={{ margin: 0, fontSize: '0.85rem' }}>
                            {searchQuery 
                                ? `No features match "${searchQuery}"`
                                : filterType !== 'all' 
                                    ? `No ${filterType} features available`
                                    : !familyId 
                                        ? 'No family selected. Please link a family to your project.'
                                        : 'No features found for this family.'}
                        </p>
                        {!familyId && linkedFamilies && linkedFamilies.length > 0 && (
                            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                Available families: {linkedFamilies.map(f => f.name).join(', ')}
                            </p>
                        )}
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                    }}>
                        {filteredFeatures.map(({ category, features: categoryFeatures }) => {
                            const metadata = inputTypeMetadata[category] || {
                                label: category,
                                icon: 'fa-solid fa-cube',
                                color: '#6b7280'
                            };
                            
                            const isExpanded = expandedCategory === category;
                            const allCategoriesExpanded = expandedCategory === null;
                            const showExpanded = isExpanded || allCategoriesExpanded;

                            return (
                                <div 
                                    key={category}
                                    style={{
                                        border: '1px solid var(--border)',
                                        borderRadius: '8px',
                                        background: 'var(--bg-primary)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Category Header */}
                                    <div 
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.75rem 1rem',
                                            cursor: 'pointer',
                                            background: 'var(--bg-secondary)',
                                            borderBottom: '1px solid var(--border)',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onClick={() => toggleCategory(category)}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <i 
                                                className={metadata.icon}
                                                style={{
                                                    color: metadata.color,
                                                    fontSize: '0.9rem'
                                                }}
                                            ></i>
                                            <span style={{
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                color: 'var(--text-primary)'
                                            }}>
                                                {metadata.label} ({categoryFeatures.length})
                                            </span>
                                            {categoryFeatures.some(f => f.isShell) && (
                                                <span 
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        background: 'var(--text-warning, #fbbf24)',
                                                        color: 'var(--bg-primary)',
                                                        padding: '0.1rem 0.4rem',
                                                        borderRadius: '4px',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase'
                                                    }}
                                                    title="Contains shell features"
                                                >
                                                    Shell
                                                </span>
                                            )}
                                        </div>
                                        <i 
                                            className={`fa-solid ${showExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                            style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: '0.8rem',
                                                transition: 'transform 0.2s'
                                            }}
                                        ></i>
                                    </div>

                                    {/* Features in Category */}
                                    {showExpanded && (
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.25rem',
                                            padding: '0.5rem'
                                        }}>
                                            {categoryFeatures.map(feature => (
                                                <div
                                                    key={feature.id || feature.name}
                                                    className="item-highlight clickable-row"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '0.5rem 0.75rem',
                                                        width: '100%'
                                                    }}
                                                    onClick={() => handleFeatureClick(feature)}
                                                    onKeyDown={(event) => {
                                                        if (event.target !== event.currentTarget) return;
                                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                                        event.preventDefault();
                                                        handleFeatureClick(feature);
                                                    }}
                                                    tabIndex={0}
                                                    role="button"
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, feature)}
                                                    title={feature.isShell
                                                        ? 'Shell feature - must be completed in Studio/Rule Builder'
                                                        : feature.description || ''}
                                                >
                                                    {/* Feature Icon */}
                                                    <div style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '6px',
                                                        background: feature.isShell 
                                                            ? 'var(--bg-warning, rgba(251, 191, 36, 0.1))'
                                                            : 'var(--bg-success, rgba(16, 185, 129, 0.1))',
                                                        color: feature.isShell ? 'var(--text-warning, #d97706)' : 'var(--text-success, #059669)',
                                                        fontSize: '0.9rem',
                                                        marginRight: '0.75rem',
                                                        flexShrink: 0
                                                    }}>
                                                        {feature.isShell ? (
                                                            <i className="fa-solid fa-file" title="Shell"></i>
                                                        ) : (
                                                            <i className={inputTypeMetadata[feature.inputType]?.icon || 'fa-solid fa-cube'}></i>
                                                        )}
                                                    </div>

                                                    {/* Feature Info */}
                                                    <div style={{
                                                        flex: 1,
                                                        minWidth: 0
                                                    }}>
                                                        <div className="row-title" style={{ fontSize: '0.85rem' }}>
                                                            {feature.displayText || feature.name}
                                                        </div>
                                                        <div className="row-muted" style={{ fontSize: '0.7rem', marginTop: '0.1rem' }}>
                                                            {feature.name}
                                                        </div>
                                                    </div>

                                                    {/* Feature Type Badge */}
                                                    <span style={{
                                                        fontSize: '0.65rem',
                                                        background: 'var(--bg-primary)',
                                                        color: 'var(--text-secondary)',
                                                        padding: '0.15rem 0.4rem',
                                                        borderRadius: '4px',
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em'
                                                    }}>
                                                        {feature.inputType || 'Unknown'}
                                                    </span>

                                                    {/* Shell Indicator */}
                                                    {feature.isShell && (
                                                        <span 
                                                            style={{
                                                                fontSize: '0.6rem',
                                                                background: 'var(--text-warning, #fbbf24)',
                                                                color: 'var(--bg-primary)',
                                                                padding: '0.1rem 0.3rem',
                                                                borderRadius: '3px',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                marginLeft: '0.25rem'
                                                            }}
                                                            title="Shell feature - incomplete"
                                                        >
                                                            Shell
                                                        </span>
                                                    )}

                                                    {/* Drag Handle */}
                                                    <div 
                                                        style={{
                                                            marginLeft: '0.5rem',
                                                            color: 'var(--text-secondary)',
                                                            cursor: 'grab',
                                                            padding: '0.25rem'
                                                        }}
                                                        title="Drag to add to page"
                                                    >
                                                        <i className="fa-solid fa-grip-vertical"></i>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Footer with Tips */}
            <div style={{
                paddingTop: '1rem',
                borderTop: '1px solid var(--border)',
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
            }}>
                <i className="fa-solid fa-lightbulb"></i>
                <span>Drag features to add them to your page, or click to select</span>
            </div>
        </div>
    );
}
